/**
 * Brand × city hub `/{cc}/{lang}/brand/:brand/:city` (web plan W3.3): one
 * page per brand–city pair that passes the ≥ 3-store indexability rule (D-5).
 *
 * Everything comes from the backend through `useBrandCityPage`; the SEO server
 * builds the head from the same query (server/routes.mjs), so the raw HTML
 * and the page agree:
 * - H1: "{Brand} Stores in {City}" (always);
 * - the brand's live / upcoming offers valid in the city;
 * - store list with name, area, address and phone when known;
 * - `?page=N` pages the live offers (LIST_PAGE_SIZE a page; `?page=1` → 301
 *   to the bare path; a page past the end says so and is `noindex`);
 * - robots from the backend's `seo.robots`;
 * - unknown brand/city pair → 404 (noindex);
 * - canonical mismatch (old slug, wrong case) → 301 to `urlPath`.
 *
 * An unknown brand/city slug pair → 404 (noindex). Every string comes from
 * the catalogue (`brandCity:page.*`).
 */
import { lazy, type ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { MapPin, Tag } from 'lucide-react';
import { PromotionCard } from '@/components/cards';
import { PageHead } from '@/components/seo/PageHead';
import { CardSkeleton, ErrorState, NoPageNotice, Pagination } from '@/components/ui';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { useBrandCityPage } from '@/hooks/useBrandCityPage';
import { useImpressionRef } from '@/hooks/useImpressionTracking';
import { formatNumber } from '@/i18n/format';
import { useTranslate } from '@/i18n/LocaleLanguage';
import type { Translate } from '@/i18n';
import {
  brandCityBrandName,
  brandCityCityName,
  brandCityHubLastPage,
  brandCityPromotionsOn,
  sitePath,
  type BrandCityHubPage,
} from '@/seo/brandCityHubs';
import { DEFAULT_LOCALE, type Locale } from '@/seo/locales';
import { brandCityPageHead } from '@/seo/pageHeads';
import { parseListPage } from '@/seo/urls';

// Lazy like every page in App.tsx, so the 404 page stays in its own chunk.
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

/** A grid cell that registers the card for impression view-tracking. */
function TrackedCell({ id, children }: { id: string; children: ReactNode }) {
  const impressionRef = useImpressionRef('promotion', id);
  return (
    <li ref={impressionRef} className="h-full *:h-full">
      {children}
    </li>
  );
}

const GRID = 'mt-4 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5';
const COUNT = 'text-base font-semibold text-ink-tertiary';
const SECTION_HEADING = 'text-xl font-extrabold tracking-tight text-ink';

/** The page's `?page=` against the hub's live promotions. */
function hubPageOf(
  search: string,
  hub: BrandCityHubPage,
): { page: number; ok: boolean; requested: string } {
  const param = parseListPage(search);
  const requested = param.raw ?? '';
  if (!param.valid) return { page: 1, ok: false, requested };
  return { page: param.page, ok: param.page <= brandCityHubLastPage(hub), requested };
}

function paginationLabels(t: Translate) {
  return {
    previous: t('common:pagination.previous'),
    next: t('common:pagination.next'),
    page: (page: number) => t('common:pagination.page', { page }),
  };
}

export interface BrandCityHubContentProps {
  hub: BrandCityHubPage;
  locale?: Locale;
}

/** The hub for one brand × city answer. Exported so tests can render any fixture. */
export function BrandCityHubContent({ hub, locale = DEFAULT_LOCALE }: BrandCityHubContentProps) {
  const { search } = useLocation();
  const t = useTranslate();
  const { page, ok, requested } = hubPageOf(search, hub);
  const head = brandCityPageHead(hub, { locale, t });
  const brand = brandCityBrandName(hub.brand);
  const city = brandCityCityName(hub.city);
  const live = brandCityPromotionsOn(hub, page);

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:py-8">
      {/* A page past the end or a malformed `?page=` does not exist (the server's 404). */}
      <PageHead head={head} noindex={!ok} />
      <Breadcrumbs crumbs={head.breadcrumbs} className="mb-4" />
      <header>
        <h1 className="text-2xl font-extrabold tracking-tight text-ink md:text-3xl">{head.h1}</h1>
      </header>

      {!ok ? (
        <NoPageNotice
          basePath={hub.urlPath}
          requested={requested}
          labels={{
            noPage: (shown) => t('common:pagination.noPage', { page: shown }),
            firstPage: t('common:pagination.firstPage'),
          }}
        />
      ) : (
        <div className="mt-8 space-y-12">
          <section aria-labelledby="brandcity-live-heading">
            <h2 id="brandcity-live-heading" className={SECTION_HEADING}>
              {t('brandCity:page.live', { brand, city })}
              {hub.livePromotions.length > 0 && (
                <span className={COUNT}>{` (${formatNumber(hub.livePromotions.length, locale)})`}</span>
              )}
            </h2>
            {live.length > 0 ? (
              <ul className={GRID}>
                {live.map((promotion) => (
                  <TrackedCell key={promotion.id} id={promotion.id}>
                    <PromotionCard promotion={promotion} variant="grid" />
                  </TrackedCell>
                ))}
              </ul>
            ) : (
              <div className="mt-4 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-line px-6 py-12 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                  <Tag size={28} className="text-primary" aria-hidden="true" />
                </span>
                <p className="font-bold text-ink">{t('brandCity:page.liveEmpty', { brand, city })}</p>
                <p className="text-sm text-ink-secondary">{t('brandCity:page.liveEmptyHint', { brand })}</p>
              </div>
            )}
            {/* Plain <a href> links to every page of live promotions, for crawlers. */}
            <Pagination
              basePath={hub.urlPath}
              page={page}
              lastPage={brandCityHubLastPage(hub)}
              label={t('brandCity:page.pages')}
              labels={paginationLabels(t)}
            />
          </section>

          {page === 1 && hub.upcomingPromotions.length > 0 && (
            <section aria-labelledby="brandcity-upcoming-heading">
              <h2 id="brandcity-upcoming-heading" className={SECTION_HEADING}>
                {t('brandCity:page.upcoming', { brand, city })}
                <span className={COUNT}>
                  {` (${formatNumber(hub.upcomingPromotions.length, locale)})`}
                </span>
              </h2>
              <ul className={GRID}>
                {hub.upcomingPromotions.map((promotion) => (
                  <TrackedCell key={promotion.id} id={promotion.id}>
                    <PromotionCard promotion={promotion} variant="grid" />
                  </TrackedCell>
                ))}
              </ul>
            </section>
          )}

          {page === 1 && hub.storeCount > 0 && hub.stores.length > 0 && (
            <section aria-labelledby="brandcity-stores-heading">
              <h2 id="brandcity-stores-heading" className={SECTION_HEADING}>
                {t('brandCity:page.stores', {
                  brand,
                  city,
                  count: formatNumber(hub.storeCount, locale),
                })}
              </h2>
              <ul className="mt-4 divide-y divide-line">
                {hub.stores.map((store) => {
                  const displayName = store.displayName || store.name;
                  const detail = [store.area, store.address].filter(Boolean).join(', ');
                  const storeUrl = store.urlPath ? sitePath(store.urlPath) : null;
                  return (
                    <li key={store.id} className="py-4">
                      <div className="flex items-start gap-3">
                        <MapPin
                          size={16}
                          className="mt-0.5 shrink-0 text-primary"
                          aria-hidden="true"
                        />
                        <div className="min-w-0">
                          {storeUrl ? (
                            <a
                              href={storeUrl}
                              className="font-semibold text-ink hover:text-primary"
                            >
                              {displayName}
                            </a>
                          ) : (
                            <span className="font-semibold text-ink">{displayName}</span>
                          )}
                          {detail && (
                            <p className="mt-0.5 text-sm text-ink-secondary">{detail}</p>
                          )}
                          {store.phoneNumber && (
                            <p className="mt-0.5 text-sm text-ink-tertiary">{store.phoneNumber}</p>
                          )}
                          {store.openingHours && store.openingHours.length > 0 && (
                            <ul className="mt-0.5 space-y-0.5 text-sm text-ink-secondary">
                              {store.openingHours.map((hours, idx) => (
                                <li key={idx}>
                                  {hours.days.join(', ')}: {hours.opens} – {hours.closes}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

/** Loading or a failed load: say what the page is and leave robots alone (web plan W0.2). */
function BrandCityHubPending({
  error,
  onRetry,
}: {
  error: string | null;
  onRetry: () => void;
}) {
  const t = useTranslate();
  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:py-8">
      <PageHead title={t('brandCity:page.loading')} keepRobots />
      {error ? (
        <ErrorState
          title={t('brandCity:page.error')}
          titleAs="h1"
          message={error}
          onRetry={onRetry}
          retryLabel={t('brandCity:page.retry')}
        />
      ) : (
        <div
          className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
          aria-busy="true"
        >
          {Array.from({ length: 8 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      )}
    </div>
  );
}

export interface BrandCityPageProps {
  /** The route's locale: routes are generated per locale with literal segments (App.tsx). */
  locale?: Locale;
}

export default function BrandCityPage({ locale = DEFAULT_LOCALE }: BrandCityPageProps) {
  const { brand, city } = useParams();
  const { pathname, search, hash } = useLocation();
  const key =
    typeof brand === 'string' && typeof city === 'string'
      ? { brand: brand.toLowerCase(), city: city.toLowerCase() }
      : null;
  const { hub, status, error, refetch } = useBrandCityPage(key, locale);

  if (status === 'not_found') return <NotFoundPage />;
  if (status !== 'ok' || !hub)
    return <BrandCityHubPending error={status === 'error' ? error : null} onRetry={refetch} />;

  // Another spelling of the hub's path (old slug, wrong case): redirect to the
  // canonical URL (the SEO server answers the same with one 301).
  const canonical = sitePath(hub.urlPath);
  if (canonical && pathname !== canonical)
    return <Navigate replace to={{ pathname: canonical, search, hash }} />;

  return <BrandCityHubContent key={hub.urlPath} hub={hub} locale={locale} />;
}
